import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Save, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer,
    ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ZAxis
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function FrameworkDetail({ user }) {
    const { frameworkId } = useParams();
    const navigate = useNavigate();

    const [framework, setFramework] = useState(null);
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isSaved, setIsSaved] = useState(false);

    useEffect(() => {
        const fetchFrameworkData = async () => {
            try {
                // Fetch schema
                const schemaRes = await axios.get(`${BACKEND_URL}/api/frameworks/${frameworkId}`, { withCredentials: true });
                setFramework(schemaRes.data);

                // Fetch existing response if any
                const responseRes = await axios.get(`${BACKEND_URL}/api/frameworks/${frameworkId}/responses`, { withCredentials: true });
                if (responseRes.data && responseRes.data.data) {
                    setFormData(responseRes.data.data);
                    setIsSaved(true);
                } else {
                    // Initialize empty defaults
                    const defaults = {};
                    schemaRes.data.fields.forEach(field => {
                        if (field.type === 'dynamic_list') {
                            defaults[field.name] = [];
                        } else {
                            defaults[field.name] = '';
                        }
                    });

                    // Hyperconnection: if Qualificacao is empty, try to load Descoberta (Friccoes)
                    if (frameworkId === 'fw_qualificacao') {
                        try {
                            const descRes = await axios.get(`${BACKEND_URL}/api/frameworks/fw_descoberta/responses`, { withCredentials: true });
                            if (descRes.data && descRes.data.data && descRes.data.data.friccoes) {
                                const friccoes = descRes.data.data.friccoes;
                                defaults.projetos = friccoes.map(f => ({
                                    projeto_ref: f.descricao || '',
                                    decisao: ''
                                }));
                                toast.success("Descobertas importadas para qualificação!");
                            }
                        } catch (e) {
                            console.log('No previous discoveries found to hydrate.', e);
                        }
                    }

                    setFormData(defaults);
                }
            } catch (error) {
                console.error('Error fetching framework detail:', error);
                toast.error("Erro ao carregar os detalhes do framework.");
            } finally {
                setLoading(false);
            }
        };
        fetchFrameworkData();
    }, [frameworkId]);

    const handleChange = (e, fieldName) => {
        const value = e.target.type === 'number' ? Number(e.target.value) : e.target.value;
        setFormData(prev => ({
            ...prev,
            [fieldName]: value
        }));
        setIsSaved(false);
    };

    const handleDynamicListChange = (index, subfieldName, value, listName) => {
        setFormData(prev => {
            const newList = [...(prev[listName] || [])];
            newList[index] = { ...newList[index], [subfieldName]: value };
            return { ...prev, [listName]: newList };
        });
        setIsSaved(false);
    };

    const handleAddDynamicItem = (listName, subfields) => {
        const newItem = {};
        subfields.forEach(sf => newItem[sf.name] = '');
        setFormData(prev => ({
            ...prev,
            [listName]: [...(prev[listName] || []), newItem]
        }));
        setIsSaved(false);
    };

    const handleRemoveDynamicItem = (index, listName) => {
        setFormData(prev => ({
            ...prev,
            [listName]: prev[listName].filter((_, i) => i !== index)
        }));
        setIsSaved(false);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.post(
                `${BACKEND_URL}/api/frameworks/${frameworkId}/responses`,
                { framework_id: frameworkId, data: formData },
                { withCredentials: true }
            );
            toast.success("Respostas salvas com sucesso!");
            setIsSaved(true);
        } catch (error) {
            console.error('Error saving framework response:', error);
            toast.error("Erro ao salvar suas respostas.");
        } finally {
            setSaving(false);
        }
    };

    const renderVisualizer = () => {
        if (!framework) return null;

        if (framework.visual_type === 'radar_chart') {
            const chartData = framework.fields.map(field => ({
                subject: field.label,
                A: formData[field.name] || 0,
                fullMark: field.max || 5,
            }));

            return (
                <div className="h-[400px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <RadarChart cx="50%" cy="50%" outerRadius="80%" data={chartData}>
                            <PolarGrid />
                            <PolarAngleAxis dataKey="subject" />
                            <PolarRadiusAxis angle={30} domain={[0, 5]} />
                            <Radar name="Maturidade" dataKey="A" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.6} />
                            <Tooltip />
                        </RadarChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        if (framework.visual_type === 'scatter_matrix') {
            const listName = framework.fields[0].name; // assuming first field is the dynamic list
            const items = formData[listName] || [];
            const chartData = items.filter(it => it.titulo).map((it, i) => ({
                nome: it.titulo,
                impacto: Number(it.impacto) || 0,
                viabilidade: Number(it.viabilidade) || 0,
                index: i + 1
            }));

            return (
                <div className="h-[400px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                            <CartesianGrid />
                            <XAxis type="number" dataKey="viabilidade" name="Viabilidade" unit="" domain={[0, 10]} />
                            <YAxis type="number" dataKey="impacto" name="Impacto" unit="" domain={[0, 10]} />
                            <ZAxis type="category" dataKey="nome" name="Nome" />
                            <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                            <Scatter name="Ideias" data={chartData} fill="hsl(var(--primary))">
                                {chartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill="hsl(var(--primary))" />
                                ))}
                            </Scatter>
                        </ScatterChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        if (framework.visual_type === 'standard' || !framework.visual_type) {
            return (
                <div className="w-full h-full flex flex-col">
                    <div className="p-6 bg-card border border-border rounded-xl shadow-sm text-left flex-1 overflow-y-auto">
                        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-border">
                            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary font-bold">
                                {framework.acronym || 'AI'}
                            </span>
                            <h4 className="font-bold text-lg">Resumo Executivo</h4>
                        </div>
                        <div className="space-y-6">
                            {framework.fields.map(field => {
                                const val = formData[field.name];
                                if (!val || (Array.isArray(val) && val.length === 0)) return null;

                                return (
                                    <div key={field.name} className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                                        <h5 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">{field.label}</h5>
                                        {Array.isArray(val) ? (
                                            <ul className="space-y-2">
                                                {val.map((item, idx) => {
                                                    const keys = Object.keys(item);
                                                    const mainText = item.titulo || item.referencia || item[keys[0]];
                                                    const subText = keys.length > 1 ? item[keys[1]] : null;
                                                    if (!mainText) return null;

                                                    return (
                                                        <li key={idx} className="bg-background/50 border border-border p-3 rounded-md text-sm">
                                                            <div className="font-semibold">{mainText}</div>
                                                            {subText && <div className="text-xs text-muted-foreground mt-1">{subText}</div>}
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        ) : (
                                            <p className="text-sm bg-background/50 border border-border p-3 rounded-md whitespace-pre-wrap">{val}</p>
                                        )}
                                    </div>
                                );
                            })}

                            {Object.keys(formData).length === 0 && (
                                <div className="text-center text-muted-foreground py-10 flex flex-col items-center">
                                    <span className="block mb-2">📄</span>
                                    O seu resumo será construído aqui em tempo real conforme você preenche os dados ao lado.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="flex items-center justify-center h-full text-muted-foreground p-8 text-center border-2 border-dashed border-border rounded-xl">
                Preencha os dados ao lado para visualizar os resultados deste framework.
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <Navigation user={user} />
                <div className="flex justify-center items-center h-[60vh]">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            </div>
        );
    }

    if (!framework) {
        return (
            <div className="min-h-screen bg-background">
                <Navigation user={user} />
                <div className="container mx-auto px-4 py-16 text-center">
                    <h2 className="text-2xl font-bold mb-4">Framework não encontrado</h2>
                    <Button onClick={() => navigate('/frameworks')}>Voltar aos Frameworks</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background flex flex-col">
            <Navigation user={user} />

            <main className="flex-1 container mx-auto px-4 md:px-8 py-8">
                <Link to="/frameworks" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors">
                    <ArrowLeft className="h-4 w-4" /> Voltar aos Frameworks
                </Link>

                <div className="mb-8">
                    <div className="flex items-center gap-3">
                        <span className="text-xs font-bold px-2 py-1 rounded bg-secondary text-secondary-foreground uppercase tracking-wider">
                            {framework.acronym}
                        </span>
                        <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {framework.name}
                        </h1>
                    </div>
                    <p className="text-muted-foreground mt-2 max-w-3xl">
                        {framework.description} {framework.purpose}
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

                    <div className="lg:col-span-5 flex flex-col h-full gap-4">
                        <Card className="p-6 flex-1 bg-card border border-border flex flex-col shadow-sm">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-xl font-bold">Entrada de Dados</h3>
                                {isSaved && <span className="text-xs flex items-center gap-1 text-green-500 font-medium bg-green-500/10 px-2 py-1 rounded-full"><CheckCircle2 className="h-3 w-3" /> Salvo</span>}
                            </div>

                            <div className="space-y-6 flex-1 overflow-y-auto pr-2">
                                {framework.fields.map((field) => (
                                    <div key={field.name} className="space-y-2">
                                        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                            {field.label}
                                        </label>

                                        {field.type === 'number' || field.type === 'text' ? (
                                            <input
                                                type={field.type}
                                                value={formData[field.name] || ''}
                                                onChange={(e) => handleChange(e, field.name)}
                                                min={field.min}
                                                max={field.max}
                                                className="flex h-10 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                                                placeholder={`Insira ${field.label.toLowerCase()}`}
                                            />
                                        ) : field.type === 'textarea' ? (
                                            <textarea
                                                value={formData[field.name] || ''}
                                                onChange={(e) => handleChange(e, field.name)}
                                                className="flex min-h-[120px] w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors resize-y"
                                                placeholder={`Descreva ${field.label.toLowerCase()}...`}
                                            />
                                        ) : field.type === 'select' ? (
                                            <select
                                                value={formData[field.name] || ''}
                                                onChange={(e) => handleChange(e, field.name)}
                                                className="flex h-10 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-colors"
                                            >
                                                <option value="" disabled>Selecione uma opção</option>
                                                {(field.options || []).map(opt => (
                                                    <option key={opt} value={opt}>{opt}</option>
                                                ))}
                                            </select>
                                        ) : field.type === 'dynamic_list' ? (
                                            <div className="space-y-4 border border-border p-4 rounded-lg bg-background/20">
                                                {(formData[field.name] || []).map((item, index) => (
                                                    <div key={index} className="flex gap-2 items-start relative group p-4 bg-card border border-border/80 shadow-sm rounded-md transition-all hover:border-border">
                                                        <button
                                                            onClick={() => handleRemoveDynamicItem(index, field.name)}
                                                            className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                                                            title="Remover item"
                                                        >
                                                            ×
                                                        </button>
                                                        <div className="flex-1 space-y-4">
                                                            {field.subfields.map(sub => (
                                                                <div key={sub.name}>
                                                                    <label className="text-xs font-bold text-muted-foreground mb-1.5 block uppercase tracking-wider">{sub.label}</label>
                                                                    {sub.type === 'textarea' ? (
                                                                        <textarea
                                                                            value={item[sub.name] || ''}
                                                                            onChange={(e) => handleDynamicListChange(index, sub.name, e.target.value, field.name)}
                                                                            className="flex min-h-[80px] w-full rounded border border-input bg-background/50 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-ring focus-visible:ring-1 resize-y transition-colors"
                                                                            placeholder={`...`}
                                                                        />
                                                                    ) : sub.type === 'select' ? (
                                                                        <select
                                                                            value={item[sub.name] || ''}
                                                                            onChange={(e) => handleDynamicListChange(index, sub.name, e.target.value, field.name)}
                                                                            className="flex h-9 w-full rounded border border-input bg-background/50 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-ring focus-visible:ring-1 transition-colors"
                                                                        >
                                                                            <option value="" disabled>Selecione...</option>
                                                                            {(sub.options || []).map(opt => (
                                                                                <option key={opt} value={opt}>{opt}</option>
                                                                            ))}
                                                                        </select>
                                                                    ) : (
                                                                        <input
                                                                            type={sub.type}
                                                                            value={item[sub.name] || ''}
                                                                            min={sub.min}
                                                                            max={sub.max}
                                                                            onChange={(e) => handleDynamicListChange(index, sub.name, e.target.type === 'number' ? Number(e.target.value) : e.target.value, field.name)}
                                                                            className="flex h-9 w-full rounded border border-input bg-background/50 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-ring focus-visible:ring-1 transition-colors"
                                                                            placeholder={sub.type === 'text' ? 'Digite...' : ''}
                                                                        />
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                                <Button
                                                    type="button"
                                                    variant="secondary"
                                                    size="sm"
                                                    className="w-full text-xs font-semibold tracking-wide border border-dashed border-border hover:bg-secondary/80"
                                                    onClick={() => handleAddDynamicItem(field.name, field.subfields)}
                                                >
                                                    + Adicionar Item
                                                </Button>
                                            </div>
                                        ) : null}
                                    </div>
                                ))}
                            </div>

                            <div className="pt-6 mt-4 border-t border-border">
                                <Button
                                    className="w-full gap-2"
                                    onClick={handleSave}
                                    disabled={saving}
                                >
                                    <Save className="h-4 w-4" />
                                    {saving ? 'Salvando...' : 'Salvar Respostas'}
                                </Button>
                            </div>
                        </Card>
                    </div>

                    <div className="lg:col-span-7 flex flex-col h-full">
                        <Card className="p-6 flex-1 bg-card border border-border shadow-sm flex flex-col min-h-[500px]">
                            <h3 className="text-xl font-bold mb-6">Visualização</h3>
                            <div className="flex-1 flex items-center justify-center bg-background/50 rounded-xl border border-border p-4">
                                {renderVisualizer()}
                            </div>
                            <div className="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/10">
                                <h4 className="text-sm font-bold mb-1 text-primary">Diagnóstico Automático</h4>
                                <p className="text-sm text-muted-foreground">
                                    Com base nestes dados consolidados, o sistema irá incorporar sua maturidade no seu <Link to="/ai-plan" className="text-primary hover:underline font-medium">Plano AI First</Link>.
                                </p>
                            </div>
                        </Card>
                    </div>

                </div>
            </main>
        </div>
    );
}
